import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import { useModal } from 'flow/contexts/ModalProvider';
import { segmentTrack } from 'flow/actions/segment-actions';
import { updateTicketAssignee } from 'flow/actions/tickets-action';
import { selectAssignee } from 'flow/selectors/TicketsSelectors';
import { TicketKey } from 'flow/types/EntityKeys';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';

import { AssigneeKey } from './useAssigneeLabel';
import useTicketAction from 'flow/hooks/useTicketAction';

const useUpdateTicketAssignee = (
    ticketKey: TicketKey,
    segmentComponent: string,
) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const runTicketAction = useTicketAction();
    const { openModal } = useModal();

    const assigneeKey = useAppSelector((state) =>
        selectAssignee(state, ticketKey),
    );

    const [isPerformingApiRequest, setIsPerformingApiRequest] = useState(false);

    const onSelect = useCallback(
        (newAssignee: AssigneeKey) => {
            if (newAssignee === assigneeKey) return;

            setIsPerformingApiRequest(true);
            dispatch(
                segmentTrack(TICKET_STRINGS.USER_ASSIGNED_TICKET, {
                    component: segmentComponent,
                }),
            );
            runTicketAction(updateTicketAssignee(ticketKey, newAssignee))
                .catch((error: any) => {
                    if (error.status === 404) {
                        openModal('confirmationDialog', {
                            title: i18next.t('tickets.eligible.title'),
                            description: i18next.t('tickets.eligible.ccpro'),
                            hideCancelButton: true,
                            handleAccept: () => {},
                        });
                    }
                })
                .finally(() => setIsPerformingApiRequest(false));
        },
        [
            dispatch,
            ticketKey,
            assigneeKey,
            openModal,
            i18next,
            segmentComponent,
        ],
    );

    return { onSelect, isPerformingApiRequest };
};

export default useUpdateTicketAssignee;
